import { LIMITS, normaliseUsername, type GameType } from '@cardengine/shared';
import { useEffect, useState } from 'react';
import { useStore, type AnyRules } from '../store.js';

/** The shape both patience games' presets already have. */
export interface SoloPreset {
  id: string;
  name: string;
  description: string;
  rules: AnyRules;
}

interface SoloStartPanelProps {
  gameType: GameType;
  gameName: string;
  presets: readonly SoloPreset[];
  /** What the row of presets is choosing between: "Difficulty", "Cells". */
  choiceLabel: string;
  /**
   * The verbs, for a game that is not dealt.
   *
   * Card language was safe here while every solo game was a patience game, and
   * Minesweeper is the first that is not — "Deal a game of Minesweeper" is the
   * sort of wrongness that makes a site feel like it was built for something
   * else and had this bolted on. Three strings rather than a `kind` check,
   * because the panel has no business knowing what a shelf is.
   */
  verb?: { heading: string; button: string; busy: string };
}

const DEALT = { heading: 'Deal a game of', button: 'Deal', busy: 'Dealing…' };

/**
 * A solo game's way in.
 *
 * No table code and nobody to wait for, so the panel collapses to a name and a
 * choice of how punishing to make it. The name still matters: the seat is a
 * real lobby seat, so a dropped connection can be resumed straight back into
 * the same game.
 */
export function SoloStartPanel({
  gameType,
  gameName,
  presets,
  choiceLabel,
  verb = DEALT,
}: SoloStartPanelProps) {
  const username = useStore((s) => s.username);
  const busy = useStore((s) => s.busy);
  const homeError = useStore((s) => s.homeError);
  const createLobby = useStore((s) => s.createLobby);
  const setHomeError = useStore((s) => s.setHomeError);

  const [name, setName] = useState(username);
  const [presetId, setPresetId] = useState(presets[0]!.id);

  useEffect(() => {
    setName((current) => current || username);
  }, [username]);

  const nameValid = normaliseUsername(name) !== null;
  const preset = presets.find((p) => p.id === presetId) ?? presets[0]!;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!nameValid) {
      setHomeError('Pick a name of 1-16 letters, numbers or spaces.');
      return;
    }
    await createLobby(name.trim(), preset.rules, gameType);
  }

  return (
    <div className="joinpanel">
      <form className="joinpanel__form" onSubmit={submit}>
        <p className="joinpanel__heading">
          {verb.heading} {gameName}
        </p>

        <label className="field">
          <span className="field__label">Your name</span>
          <input
            className="field__input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={LIMITS.usernameMax}
            placeholder="e.g. Amy"
            autoComplete="nickname"
          />
        </label>

        <fieldset className="field">
          <legend className="field__label">{choiceLabel}</legend>
          <div className="presets__row">
            {presets.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`presets__button ${option.id === presetId ? 'is-active' : ''}`}
                onClick={() => setPresetId(option.id)}
                title={option.description}
              >
                {option.name}
              </button>
            ))}
          </div>
          <p className="presets__description">{preset.description}</p>
        </fieldset>

        {homeError && <p className="joinpanel__error">{homeError}</p>}

        <div className="joinpanel__actions">
          <button type="submit" className="btn btn--primary btn--large" disabled={busy || !nameValid}>
            {busy ? verb.busy : verb.button}
          </button>
        </div>
      </form>
    </div>
  );
}
